import { useState, useEffect } from 'react'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'

export default function Contacts() {
    const { profile } = useAuth()
    const isMaster = profile?.user_role === 'master'

    const [contacts, setContacts] = useState([])
    const [companies, setCompanies] = useState([])
    const [empresaSelecionada, setEmpresaSelecionada] = useState(null)
    const [showForm, setShowForm] = useState(false)
    const [form, setForm] = useState({ name: '', email: '', phone: '', role: '', company_id: '' })

    const getCompanyId = () => isMaster ? empresaSelecionada : profile?.company_id

    useEffect(() => {
        if (isMaster) {
            api.get('/companies').then(r => setCompanies(r.data))
        }
    }, [isMaster])

    useEffect(() => {
        const cId = getCompanyId()
        if (!cId) {
            setContacts([])
            return
        }
        api.get('/contacts', { params: { company_id: cId } }).then(r => setContacts(r.data))
    }, [empresaSelecionada, profile, isMaster]) // eslint-disable-line

    const handleCreate = async () => {
        if (!form.name) return
        const cId = getCompanyId()
        const payload = { ...form, company_id: cId || form.company_id || null }
        const { data } = await api.post('/contacts', payload)
        setContacts(prev => [data, ...prev])
        setForm({ name: '', email: '', phone: '', role: '', company_id: '' })
        setShowForm(false)
    }

    const handleDelete = async (id) => {
        await api.delete(`/contacts/${id}`)
        setContacts(prev => prev.filter(c => c.id !== id))
    }

    const companyId = getCompanyId()
    const empresaAtual = companies.find(e => e.id === empresaSelecionada)
    const inputClass = "w-full bg-gray-800 rounded-lg px-3 py-2 mb-3 text-sm outline-none focus:ring-2 ring-indigo-500"

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Contatos</h1>
                {companyId && (
                    <button onClick={() => setShowForm(true)} className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg text-sm font-semibold transition">+ Novo Contato</button>
                )}
            </div>

            {/* Filtro master por empresa */}
            {isMaster && (
                <div className="flex flex-wrap items-center gap-3 mb-6 bg-gray-900 rounded-xl p-3 border border-gray-800">
                    <span className="text-sm text-gray-400">🏢 Empresa:</span>
                    <select
                        className="bg-gray-800 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 ring-indigo-500 flex-1 min-w-0"
                        value={empresaSelecionada || ''}
                        onChange={e => setEmpresaSelecionada(e.target.value || null)}
                    >
                        <option value="">Selecione uma empresa</option>
                        {companies.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                    {empresaSelecionada && <button onClick={() => setEmpresaSelecionada(null)} className="text-xs text-gray-500 hover:text-white transition">✕</button>}
                    {empresaAtual && <span className="text-xs text-indigo-400 bg-indigo-900/30 px-3 py-1 rounded-full">🏢 {empresaAtual.name}</span>}
                </div>
            )}

            {/* Aviso para selecionar empresa (master) */}
            {isMaster && !companyId && (
                <div className="text-center py-16 text-gray-500">
                    <p className="text-3xl mb-3">🏢</p>
                    <p>Selecione uma empresa para ver os contatos.</p>
                </div>
            )}

            {/* Modal Novo Contato */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
                    <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md border border-gray-700">
                        <h2 className="text-lg font-bold mb-4">Novo Contato</h2>
                        {empresaAtual && <p className="text-xs text-indigo-400 mb-3">🏢 {empresaAtual.name}</p>}
                        {!isMaster && profile?.company_id && companies.length === 0 && (
                            <p className="text-xs text-indigo-400 mb-3">🏢 Sua empresa</p>
                        )}
                        <input className={inputClass} placeholder="Nome *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                        <input className={inputClass} placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                        <input className={inputClass} placeholder="Telefone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                        <input className={inputClass} placeholder="Cargo" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} />
                        <div className="flex gap-2">
                            <button onClick={handleCreate} className="flex-1 bg-indigo-600 hover:bg-indigo-700 py-2 rounded-lg text-sm font-semibold transition">Criar</button>
                            <button onClick={() => setShowForm(false)} className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded-lg text-sm transition">Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Lista de contatos */}
            {companyId && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {contacts.map(c => (
                        <div key={c.id} className="bg-gray-900 rounded-xl p-4 border border-gray-800 hover:border-indigo-500/50 transition">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="font-semibold">{c.name}</h3>
                                    {c.role && <p className="text-xs text-gray-400">{c.role}</p>}
                                    {c.companies && <span className="text-xs text-indigo-400">{c.companies.name}</span>}
                                </div>
                                <button onClick={() => handleDelete(c.id)} className="text-gray-600 hover:text-red-400 transition text-sm">✕</button>
                            </div>
                            {c.email && <p className="text-gray-400 text-sm mt-2">📧 {c.email}</p>}
                            {c.phone && <p className="text-gray-400 text-sm">📞 {c.phone}</p>}
                        </div>
                    ))}
                    {contacts.length === 0 && (
                        <p className="text-gray-500 col-span-3 text-center py-12">Nenhum contato cadastrado ainda.</p>
                    )}
                </div>
            )}
        </div>
    )
}